import {
  LayerId,
  SerializedLayer,
  SerializedLayers,
  SerializedPage,
} from '@lidojs/design-core';
import { getImpressionTagHTML } from '@lidojs/design-editor';
import { extractLinks } from '../extractLinks';
import { XMobileFrameHTML } from '../htmlComponents';
import { scriptClickthroughRedirect, scriptPollClicks } from '../scriptsToExport';
import { mobileFrameStyles } from '../stylesToExport';
import { generateColector } from './templates/colector';
import { generateDepthCard } from './templates/depthCard';
import { generateFlipSlides } from './templates/flipSlides';
import { generateFlipUp } from './templates/flipUp';
import { generateOrb } from './templates/orb';
import { generateRotate } from './templates/rotate';
import { generateScratch } from './templates/scratch';
import { generateVideoBox } from './templates/videoBox';
import { generateVideoboxCarousel } from './templates/videoboxCarousel';


export type PageExport = {page: SerializedLayers, hasMobileFrame: boolean, pages?: SerializedPage[]}
export type PageHandler = ({page, hasMobileFrame, pages}:  PageExport) => (Promise<string> | string);

const handlers: Record<string, PageHandler> = {
  depthCard: generateDepthCard,
  colector: generateColector,
  flipUp: generateFlipUp,
  rotate: generateRotate,
  scratch: generateScratch,
  flipSlides: generateFlipSlides,
  videobox: generateVideoBox,
  videoboxCarousel: generateVideoboxCarousel,
  orb: generateOrb,
};

export const generatePremiumHTML = async ({
  page,
  hasMobileFrame,
  pages,
}: {
  page: Record<LayerId, SerializedLayer>;
  hasMobileFrame?: boolean;
  pages?: SerializedPage[];
}): Promise<string> => {
  const { ROOT: container } = page;

  const pageId = container.idPremium;
  const handler = await handlers[pageId];
  const links = extractLinks(page);

  if (handler) {
    const htmlGenerated = await handler({page, hasMobileFrame, pages});
    const impressionTagHTML = getImpressionTagHTML(container?.props?.impressionTag as { hasImpressionTag?: boolean; tagContent?: string } | undefined);
    return htmlParentPremium({
      html: htmlGenerated,
      hasMobileFrame,
      links,
      impressionTagHTML,
    });
  } else {
    throw new Error(`No handler found for page ID: ${pageId}`);
  }
};

const htmlParentPremium = ({html, hasMobileFrame, links, impressionTagHTML}:{html: string, hasMobileFrame?: boolean, links: string, impressionTagHTML?: string}): string => {
  return `
        <!DOCTYPE html>
  <html lang="es">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0, interactive-widget=resizes-content">
    <meta name="ad.size" content="width=320,height=480">

    <style>
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
        -webkit-user-select: none; /* Safari */
        -ms-user-select: none; /* IE 10 and IE 11 */
        user-select: none; /* Standard syntax */
      }
    </style>
    ${hasMobileFrame ? `<style>${mobileFrameStyles}</style>` : ''}
      ${html}
      <script>
        ${links}
        ${scriptClickthroughRedirect}
      </script>
      ${scriptPollClicks}
      ${impressionTagHTML ?? ''}
    </body>
  </html>
  `;
};

export const containerStyleSize = (hasMobileFrame: boolean) => {
  if (hasMobileFrame) {
    return `width: 100%; height: auto;`;
  }
  return `height: 100dvh; width: auto;`;
};

export const containerPremium = (hasMobileFrame: boolean) => {
  return `
  <div style="width: 100%; height: 100%; min-height: 100dvh; display: flex; justify-content: center; align-items: center">
    ${hasMobileFrame ? `<div class="mobile-preview">` : ''}
        <div
      data-locked="false"
      data-id="ROOT"
      data-type="RootLayer"
      data-child="[]"
      data-parent="null"
      id="ROOT"
      style="${containerStyleSize(hasMobileFrame)}
      aspect-ratio: 1080 / 1920;
      position: relative;
      overflow: hidden; background-color: transparent;">
      ${hasMobileFrame ? XMobileFrameHTML : ""}`
}
